import { useMemo } from "react"

import { useSkuStore } from "~/providers/sku-store-provider"
import { api } from "~/utils/api"
import { filterDefined } from "~/utils/tsUtils"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { useFilters } from "~/hooks/useFilters"
import { useFilterFieldTypeResolver } from "~/hooks/useFilterFieldTypeResolver"
import { filterVariants } from "~/utils/filter"
import { useIncomingOrders } from "~/hooks/useVariantTableHooks"
import { useSkipSkuStoreData } from "~/hooks/useSkipSkuStoreData"
import { type IncomingOrder } from "~/utils/incomingOrdersUtils"

export const useVariantMap = () => {
  return useSkuStore((state) => state.newVariantMap)
}

export type SkuStoreEntry = { variant: IVariantSummary; size: string }

/** skuId → its variant and size, from the SKU store: shown variants plus unshown phasing-out ones. */
export const useSkuMap = (): ReadonlyMap<string, SkuStoreEntry> => {
  const variants = useSkuStore((state) => state.variants)
  return useMemo(() => {
    const bySku = new Map<string, SkuStoreEntry>()
    for (const variant of variants)
      for (const sku of variant.skuData) bySku.set(sku.skuId, { variant, size: sku.size })
    return bySku
  }, [variants])
}

export type IVariantNameEntry = {
  variantId: string
  variantName: string | undefined
  productId: string
  productName: string
  imageUrl: string | undefined
  color: string | undefined
}

/** Variant/product names for lookup (search, @-mentions): SKU store when hydrated, else a light fetch. */
export const useVariantNameList = () => {
  const skipSkuStoreData = useSkipSkuStoreData()
  const storeVariants = useSkuStore((state) => state.variants)
  const { data, ...props } = api.variantTableData.variantNameList.useQuery(undefined, {
    enabled: skipSkuStoreData,
  })
  const memoizedData = useMemo<IVariantNameEntry[]>(() => {
    const toEntry = (v: {
      variantId: string
      variantName?: string | null
      productId?: string | null
      productName?: string | null
      imageUrl?: string | null
      color?: string | null
    }): IVariantNameEntry => ({
      variantId: v.variantId,
      variantName: v.variantName ?? undefined,
      productId: v.productId ?? "",
      productName: v.productName ?? "",
      imageUrl: v.imageUrl ?? undefined,
      color: v.color ?? undefined,
    })
    if (skipSkuStoreData) return (data ?? []).map(toEntry)
    return storeVariants.filter((v) => v.shouldBeShown).map(toEntry)
  }, [skipSkuStoreData, data, storeVariants])
  return { data: memoizedData, ...props }
}

export const useVariants = () => {
  const variants = useSkuStore((state) => state.variants)
  return useMemo(() => variants.filter((variant) => variant.shouldBeShown), [variants])
}

/**
 * useVariants() plus the given match variants even when hidden — a confirmed
 * match to a hidden variant must still render with its variant data.
 */
export const useVariantsWithMatches = (
  matches: Array<{ match: string }> | undefined,
): IVariantSummary[] => {
  const variants = useVariants()
  const matchIds = useMemo(() => matches?.map((m) => m.match) ?? [], [matches])
  const matchVariants = useVariantsByIds(matchIds)
  return useMemo(() => {
    const extras = matchVariants.filter((v) => !v.shouldBeShown)
    return extras.length > 0 ? [...variants, ...extras] : variants
  }, [variants, matchVariants])
}

export const useNpiVariants = () => {
  const variants = useSkuStore((state) => state.variants)
  return useMemo(() => variants.filter((v) => v.isNpi && v.shouldBeShown), [variants])
}

export const useVariantsByIds = (ids: string[] = []): IVariantSummary[] => {
  const variantMap = useSkuStore((state) => state.newVariantMap)
  return useMemo(() => filterDefined(ids.map((id) => variantMap.get(id))), [ids, variantMap])
}

export const usePageFilteredVariants = (variants: IVariantSummary[]): IVariantSummary[] => {
  const { filters } = useFilters()
  const getFieldType = useFilterFieldTypeResolver()
  return useMemo(
    () => filterVariants(filters, variants, undefined, getFieldType),
    [filters, variants, getFieldType],
  )
}

export const useLinkedVariants = () => {
  const variants = useVariants()
  const { data: allLinkedVariants, isLoading } = api.replacementProduct.getLinkedVariants.useQuery()

  return useMemo(
    () => (isLoading ? [] : variants.filter((v) => allLinkedVariants?.has(v.variantId))),
    [variants, allLinkedVariants, isLoading],
  )
}

/**
 * Merges status="arrived" orders from arrivedOrdersMap into each variant's incomingOrders.
 * Filters to status="arrived" to exclude PurchaseOrder records that the query returns via
 * its UNION ALL (those are already in incomingOrders from the store).
 */
function addArrivedIncomingOrders<T extends IVariantSummary>(
  variants: T[],
  arrivedOrdersMap: Map<string, IncomingOrder[]> | undefined,
): T[] {
  if (!arrivedOrdersMap) return variants
  return variants.map((v) => ({
    ...v,
    incomingOrders: [
      ...(v.incomingOrders ?? []),
      ...(arrivedOrdersMap.get(v.variantId) ?? []).filter((o) => o.status === "arrived"),
    ],
  })) as T[]
}

export function useFilteredVariants(
  searchText?: string,
  options?: {
    fetchArrivedOrders?: boolean
    filtersEnabled?: boolean
  },
): IVariantSummary[] {
  const { data: arrivedOrdersMap } = useIncomingOrders(
    options?.fetchArrivedOrders ?? false,
    "arrived",
  )
  const variants = useVariants()
  const mergedVariants = addArrivedIncomingOrders(variants, arrivedOrdersMap ?? undefined)
  const { filters } = useFilters()
  const effectiveFilters = (options?.filtersEnabled ?? true) ? filters : []
  const getFieldType = useFilterFieldTypeResolver()

  return useMemo(
    () => filterVariants(effectiveFilters, mergedVariants, searchText, getFieldType),
    [effectiveFilters, mergedVariants, searchText, getFieldType],
  )
}
